import { Camera, Circle, Square } from 'lucide-react';
import { useRef, useState } from 'react';
import { useUiStore } from '@/stores/uiStore';

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * A screenshot button (reads the WebGL canvas's own pixels via `toBlob` — no
 * DOM-to-image library needed) and a WebM recorder (`canvas.captureStream` +
 * `MediaRecorder`, both native browser APIs), floated over the 3D viewport.
 */
export function ViewportCaptureControls({ containerRef }: { containerRef: React.RefObject<HTMLDivElement | null> }) {
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const getCanvas = (): HTMLCanvasElement | null => containerRef.current?.querySelector('canvas') ?? null;

  const handleScreenshot = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) triggerDownload(blob, `viewport-${Date.now()}.png`);
    }, 'image/png');
  };

  const handleToggleRecording = () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    const canvas = getCanvas();
    if (!canvas) return;

    const stream = canvas.captureStream(30);
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      triggerDownload(new Blob(chunksRef.current, { type: 'video/webm' }), `viewport-recording-${Date.now()}.webm`);
      setRecording(false);
    };
    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
    useUiStore.getState().notify({ kind: 'info', title: 'Recording started', message: 'Click again to stop and download.' });
  };

  return (
    <div className="absolute right-2 top-2 z-10 flex gap-1">
      <button
        type="button"
        onClick={handleScreenshot}
        title="Save a screenshot"
        aria-label="Save viewport screenshot"
        className="flex h-7 w-7 items-center justify-center rounded-control border border-border bg-surface/80 text-muted backdrop-blur hover:text-text"
      >
        <Camera className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={handleToggleRecording}
        title={recording ? 'Stop recording' : 'Record a WebM clip'}
        aria-label={recording ? 'Stop viewport recording' : 'Start viewport recording'}
        className="flex h-7 w-7 items-center justify-center rounded-control border border-border bg-surface/80 text-muted backdrop-blur hover:text-text"
      >
        {recording ? <Square className="h-3.5 w-3.5 text-danger" /> : <Circle className="h-4 w-4" />}
      </button>
    </div>
  );
}
